import { objectKeysToSnakeCase } from '@mint-lib/api';
import { defaultApi } from '@mint-sdk/api-core';
import { z } from 'zod';

import { ExportTagsRequestSchema } from '../shared/schemas/trackerMappings.js';

export { PlacementActivityStatusEnum } from '../shared/schemas/trackerMappings.js';

export type ExportTagsRequest = z.infer<typeof ExportTagsRequestSchema>;

export type ExportTagsFileType = 'csv' | 'xlsx';

type ExportTagsResponse = {
  blob: Blob;
  fileName: string;
  contentType: string;
};

const parseFileName = (contentDisposition: string | undefined): string => {
  if (!contentDisposition) return 'exported_tags';

  const filenameRegex = /filename\*?=(?:UTF-8''|")(.*?)(?:"|$)/;
  const match = filenameRegex.exec(contentDisposition);

  const raw = match?.[1] ?? contentDisposition.split('=')[1] ?? 'exported_tags';

  let trimmed = raw.trim();
  while (trimmed.startsWith('"')) {
    trimmed = trimmed.slice(1);
  }
  while (trimmed.endsWith('"')) {
    trimmed = trimmed.slice(0, -1);
  }
  return trimmed;
};

export const exportTags = async (
  fileType: ExportTagsFileType,
  payload: ExportTagsRequest,
): Promise<ExportTagsResponse | null> => {
  const validatedPayload = ExportTagsRequestSchema.parse(payload);
  const snakeCasePayload = objectKeysToSnakeCase(validatedPayload);

  const client = defaultApi.getClient();

  const response = await client.request({
    url: `/api/omni-trafficking/v1/tracker-mappings/export_tags/${fileType}/`,
    method: 'POST',
    data: snakeCasePayload,
    responseType: 'blob',
    headers: {
      'User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      Accept: 'text/csv, */*',
    },
  });

  if (response.status === 204) {
    return null;
  }

  const getHeader = (name: string): string | undefined => {
    if (response.headers instanceof Headers) {
      return response.headers.get(name) || undefined;
    }
    return response.headers[name.toLowerCase()] || response.headers[name];
  };

  const headerContentType = getHeader('content-type');
  const parsedContentType = headerContentType
    ? headerContentType.toString().split(';')[0].trim()
    : '';

  const contentType =
    parsedContentType ||
    (fileType === 'csv'
      ? 'text/csv'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

  // @ts-expect-error TS2339 There is issue with blob file and dataToCamelCase interceptor
  const blob = new Blob([response.request.response], {
    type: contentType,
  });

  const fileName = parseFileName(getHeader('content-disposition'));

  return {
    blob,
    fileName,
    contentType,
  };
};
